import { useState } from 'react';
import { AlertTriangle, CheckCircle, AlertCircle, ChevronDown, FileCheck } from 'lucide-react';
import { readAsIssuedMarker } from '@/lib/masterReferenceStaleness';
import { formatDate } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const RiskBadge = ({ level, score }) => {
  const config = {
    'Low Risk': {
      bg: 'bg-[#E8F5E9]',
      text: 'text-[#1B5E20]',
      border: 'border-[#A5D6A7]',
      icon: CheckCircle
    },
    'Medium Risk': {
      bg: 'bg-[#FFF3E0]',
      text: 'text-[#E65100]',
      border: 'border-[#FFCC80]',
      icon: AlertCircle
    },
    'High Risk': {
      bg: 'bg-[#FFEBEE]',
      text: 'text-[#B71C1C]',
      border: 'border-[#EF9A9A]',
      icon: AlertTriangle
    }
  };

  const { bg, text, border, icon: Icon } = config[level] || config['Medium Risk'];

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 ${bg} ${text} ${border} border`}>
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="text-sm font-medium">{level}</span>
      {score !== undefined && (
        <span className="text-sm opacity-75">({Math.round(score)})</span>
      )}
    </div>
  );
};

// THE AS-ISSUED MARKER (staleness gate, commit 5). Rendered on every risk
// surface - the wizard's Risk step (card and sticky header), the detail page
// (card and header badge) and the dashboard card - from the marker STAMPED
// on the stored assessment at generation. It takes the assessment and nothing
// else: no advisory, no master list, no category lookup. A marker recomputed
// from the live master would vanish on reactivation, precisely when the
// record matters most (backend routes/_risk_category_attestation.py).
export const RiskAttestationMarker = ({ riskAssessment, compact = false }) => {
  const marker = readAsIssuedMarker(riskAssessment);
  if (!marker) return null;
  const codes = (marker.retired_codes || []).join(', ');
  // WHICH references were attested (ruling 3, 2026-09-16). A marker stamped
  // before then carries retired_codes alone, which were categories. The
  // category wording is unchanged when only categories were attested.
  const attested = Array.isArray(marker.attested_references) ? marker.attested_references : [];
  const locations = attested.filter((r) => r && r.master === 'location_master').map((r) => r.code);
  const categories = attested.length
    ? attested.filter((r) => r && r.master !== 'location_master').map((r) => r.code)
    : (marker.retired_codes || []);
  const described = [
    categories.length ? `category ${categories.join(', ')}` : null,
    locations.length ? `ship-to location ${locations.join(', ')}` : null,
  ].filter(Boolean).join(' and ');
  const underWhat = locations.length ? described : `category ${codes}`;
  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded"
        title={`Generated for the RFP as issued, under retired ${underWhat}`}
        data-testid="risk-as-issued-badge"
      >
        <FileCheck className="w-3 h-3" /> As issued
      </span>
    );
  }
  return (
    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="risk-as-issued-marker">
      <FileCheck className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-amber-900">
        <span className="font-medium">Generated for the RFP as issued.</span>{' '}
        {locations.length
          ? `The ${described} it was generated under ${attested.length > 1 ? 'were' : 'was'} no longer active in the procurement master; the buyer`
          : `The category it was generated under (${codes}) was no longer active in the procurement master; the buyer`}
        confirmed the RFP had already been issued{marker.attested_at ? ` (${formatDate(marker.attested_at)})` : ''}.
      </p>
    </div>
  );
};

// One row of the domain breakdown table. Assessment narrative is clamped to
// ~2 lines by default; the chevron expands it in place.
const DomainRow = ({ domain, expanded, onToggle }) => (
  <TableRow>
    <TableCell className="text-base font-medium text-zinc-800 align-top">{domain.name}</TableCell>
    <TableCell className="text-zinc-900 font-semibold align-top whitespace-nowrap">{domain.score}/10</TableCell>
    <TableCell className="text-zinc-500 align-top whitespace-nowrap">{domain.weight}%</TableCell>
    <TableCell className="align-top">
      {domain.justification ? (
        <button
          type="button"
          onClick={onToggle}
          className="flex items-start gap-1.5 text-left w-full"
          data-testid={`toggle-domain-${domain.domain_id}`}
        >
          <ChevronDown
            className={`w-4 h-4 mt-0.5 shrink-0 text-zinc-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
          <span className={`text-base text-zinc-600 ${expanded ? '' : 'line-clamp-2'}`}>
            {domain.justification}
          </span>
        </button>
      ) : (
        <span className="text-zinc-400">—</span>
      )}
    </TableCell>
  </TableRow>
);

// showScoreHeader is false on the wizard's Risk Intelligence step, which
// shows the score/badge in its own sticky header instead (see StepRisk.jsx)
// - kept true by default for ProcurementDetail's Risk tab, which has no
// equivalent header of its own.
export const RiskScoreCard = ({ riskAssessment, showScoreHeader = true }) => {
  const [expandedDomains, setExpandedDomains] = useState(() => new Set());

  if (!riskAssessment) return null;

  const { risk_level, risk_score, justification, key_risk_areas, recommendations, domain_scores, weighted_total } = riskAssessment;

  const toggleDomain = (domainId) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domainId)) {
        next.delete(domainId);
      } else {
        next.add(domainId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <RiskAttestationMarker riskAssessment={riskAssessment} />
      {/* Score Header */}
      {showScoreHeader && (
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Risk Score
            </div>
            <h3 className="text-3xl font-bold text-zinc-900 tracking-tight">
              {risk_score !== undefined ? `${Math.round(risk_score)}/100` : 'N/A'}
            </h3>
            {weighted_total !== undefined && (
              <p className="text-xs text-zinc-500 mt-1">Weighted total: {Math.round(weighted_total)}/10</p>
            )}
          </div>
          <RiskBadge level={risk_level} />
        </div>
      )}

      {/* Justification */}
      {justification && (
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Assessment Summary
          </div>
          <p className="text-zinc-600 leading-relaxed">{justification}</p>
        </div>
      )}

      {/* Per-Domain Risk Breakdown - scrollable table, sticky header */}
      {domain_scores && domain_scores.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">
            Risk Domain Breakdown
          </div>
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <div className="overflow-y-auto max-h-96">
              <Table containerClassName="relative w-full overflow-visible">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Domain</TableHead>
                    <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Score</TableHead>
                    <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Weight</TableHead>
                    <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Assessment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domain_scores.map((d) => (
                    <DomainRow
                      key={d.domain_id}
                      domain={d}
                      expanded={expandedDomains.has(d.domain_id)}
                      onToggle={() => toggleDomain(d.domain_id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* Key Risk Areas */}
      {key_risk_areas && key_risk_areas.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">
            Key Risk Areas
          </div>
          <div className="space-y-2">
            {key_risk_areas.map((area, index) => (
              <div
                key={`risk-${index}-${area.slice(0, 15)}`}
                className="flex items-start gap-2 text-sm text-zinc-600"
              >
                <span className="text-zinc-400">•</span>
                <span>{area}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">
            Recommendations
          </div>
          <div className="space-y-2">
            {recommendations.map((rec, index) => (
              <div
                key={`rec-${index}-${rec.slice(0, 15)}`}
                className="flex items-start gap-2 text-sm text-zinc-600"
              >
                <span className="text-[#0055FF] font-bold">{index + 1}.</span>
                <span>{rec}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskScoreCard;
